import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { useCallback } from "react";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";

import { Button } from "@/components/ui/button/button";

import { LayoutSettingsIslandContentRows } from "./blocks";
import { DialogSnackLayoutSettings } from "./dialog/layout";
import { SnackLayoutSettingsDialogDetailSelectLayout } from "./dialog/layout/select-layout";

import type { SnackBlocksFormModelMethodsType } from "../../../../../model/usecase/use-create-snack-layout-settings-form-model/methods";
import type { SnackBlocksLayoutEnumType } from "../../../../../model/usecase/use-create-snack-layout-settings-form-model/validation-schema";
import type { DragEndEvent } from "@dnd-kit/core";

type Props = {
	hotelID: string;
	contentsMethods: SnackBlocksFormModelMethodsType["contentsMethods"];
};

export const LayoutSettingsIslandContents = ({ hotelID, contentsMethods }: Props) => {
	const {
		selectBlockCountModalState: {
			isShowSelectBlockCountModal,
			deactivateSelectBlockCountModal,
			handleOpenChangeSelectBlockCountModal,
		},
		blocks,
		onSetBlocks,
		onAddBlock,
		lastBlock,

		onSetBlockLayoutType: _onSetBlockLayoutType,
	} = contentsMethods;

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event;
			if (active && over) {
				const activeIndex = blocks.value.findIndex((item) => item.uuid.value === active.id);
				const overIndex = blocks.value.findIndex((item) => item.uuid.value === over.id);
				const newValue = [...blocks.value];
				const [removed] = newValue.splice(activeIndex, 1);
				newValue.splice(overIndex, 0, removed);

				onSetBlocks({ blocksUUIDs: newValue.map((block) => block.uuid.value) });
			}
		},
		[blocks.value, onSetBlocks],
	);

	const onSetBlockLayoutType = useCallback(
		(input: { layoutType: SnackBlocksLayoutEnumType; targetUUID: string }) => {
			_onSetBlockLayoutType(input);
			deactivateSelectBlockCountModal();
			notifyUserEventInfo({
				title: "レイアウトを設定しました",
			});
		},
		[deactivateSelectBlockCountModal, _onSetBlockLayoutType],
	);

	return (
		<DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd}>
			<div className="flex justify-center">
				<LayoutSettingsIslandContentRows hotelID={hotelID} contentsMethods={contentsMethods} />
			</div>

			<div className="flex justify-center pt-10">
				<Button type="button" variant="outline" onClick={onAddBlock}>
					コンテンツを追加
				</Button>
			</div>

			<DialogSnackLayoutSettings
				open={isShowSelectBlockCountModal}
				handleOpenChange={handleOpenChangeSelectBlockCountModal}
				handleClickCloseButton={deactivateSelectBlockCountModal}
			>
				<SnackLayoutSettingsDialogDetailSelectLayout
					uuid={lastBlock?.uuid}
					onSetBlockLayoutType={onSetBlockLayoutType}
				/>
			</DialogSnackLayoutSettings>
		</DndContext>
	);
};

LayoutSettingsIslandContents.displayName = "LayoutSettingsIslandContents";
